import React, { useEffect, useState, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Table, Spin, Pagination, Input, Button, Switch, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { useSearchBehavior } from '../hooks/useSearchBehavior';
import { GET_PAYMENT_CONDITIONS, CREATE_PAYMENT_CONDITION, UPDATE_PAYMENT_CONDITION, UPDATE_PAYMENT_CONDITION_STATUS } from '../mutations/paymentConditions';
import PaymentConditionForm from '../components/paymentConditions/PaymentConditionForm';
import { gql } from '@apollo/client';

const PaymentConditions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Usa o hook customizado para gerenciar o comportamento de busca
  const {
    searchTerm,
    setSearchTerm,
    activeSearchTerm,
    handleManualSearch,
    clearSearch,
    isInstantSearch
  } = useSearchBehavior("");
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [selectedPaymentCondition, setSelectedPaymentCondition] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const searchInputRef = useRef(null);

  // Remove o useEffect do debounce, agora é gerenciado pelo hook customizado

  const { loading, error, data, refetch } = useQuery(GET_PAYMENT_CONDITIONS, {
    variables: { page: currentPage, perPage: pageSize, search: activeSearchTerm },
    fetchPolicy: 'network-only',
  });

  const [createPaymentCondition] = useMutation(CREATE_PAYMENT_CONDITION, {
    update: (cache, { data: { createPaymentCondition } }) => {
      if (createPaymentCondition?.paymentCondition) {
        const { paymentCondition } = createPaymentCondition;
        cache.modify({
          fields: {
            paymentConditions(existingPaymentConditions = {}) {
              const newPaymentConditionRef = cache.writeFragment({
                data: paymentCondition,
                fragment: gql`
                  fragment NewPaymentCondition on PaymentCondition {
                    id
                    name
                    installments
                    inCash
                    createdAt
                    updatedAt
                    active
                  }
                `
              });
              return {
                ...existingPaymentConditions,
                edges: [...existingPaymentConditions.edges, { node: newPaymentConditionRef }]
              };
            }
          }
        });
      }
    }
  });

  const [updatePaymentCondition] = useMutation(UPDATE_PAYMENT_CONDITION);
  const [updatePaymentConditionStatus] = useMutation(UPDATE_PAYMENT_CONDITION_STATUS);

  const handleSearch = () => {
    if (!isInstantSearch) {
      handleManualSearch();
    }
    setCurrentPage(1);
  };

  const handleCreatePaymentCondition = () => {
    setSelectedPaymentCondition({active: true});
    setIsEditMode(false);
    setIsFormModalVisible(true);
  };

  const handleEditPaymentCondition = (paymentCondition) => {
    setSelectedPaymentCondition(paymentCondition);
    setIsEditMode(true);
    setIsFormModalVisible(true);
  };

  const handleFormCancel = () => {
    setIsFormModalVisible(false);
    setSelectedPaymentCondition(null);
    setErrorMessage(null);
  };

  const handleSave = async (values) => {
    setErrorMessage(null);
    try {
      let response;
      if (isEditMode) {
        response = await updatePaymentCondition({
          variables: {
            id: selectedPaymentCondition.id,
            attributes: values
          }
        });
      } else {
        response = await createPaymentCondition({
          variables: { attributes: values }
        });
      }

      const errors = response.data?.createPaymentCondition?.errors || response.data?.updatePaymentCondition?.errors;
      if (errors && errors.length > 0) {
        setErrorMessage(errors.join(', '));
      } else {
        refetch(); // Refetch payment conditions to update the list
        handleFormCancel(); // Close the modal
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Ocorreu um erro inesperado.');
    }
  };

  const showConfirmModal = (paymentCondition) => {
    setSelectedPaymentCondition(paymentCondition);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setIsModalVisible(false);
    const { id, active } = selectedPaymentCondition;
    try {
      await updatePaymentConditionStatus({
        variables: { id, active: !active },
        update: (cache, { data: { updatePaymentConditionStatus } }) => {
          const { paymentCondition } = updatePaymentConditionStatus;
          cache.modify({
            id: cache.identify(paymentCondition),
            fields: {
              active(cachedActive) {
                return !active;
              }
            }
          });
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (error) return <p>Error: {error.message}</p>;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Parcelas',
      dataIndex: 'installments',
      key: 'installments',
    },
    {
      title: 'À Vista',
      dataIndex: 'inCash',
      key: 'inCash',
      render: (inCash) => (inCash ? 'Sim' : 'Não'),
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: (text) => format(new Date(text), 'dd/MM/yyyy - HH:mm'),
    },
    {
      title: 'Atualizado em',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => format(new Date(text), 'dd/MM/yyyy HH:mm'),
    },
    {
      title: 'Ativo',
      dataIndex: 'active',
      key: 'active',
      align: 'center',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={() => showConfirmModal(record)}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (text, record) => (
        <Button type="link" onClick={() => handleEditPaymentCondition(record)}>Editar</Button>
      ),
    },
  ];

  const paymentConditions = data?.paymentConditions?.edges.map(edge => edge.node) || [];
  const { totalCount } = data?.paymentConditions || { pageInfo: {}, totalCount: 0 };

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Input
          ref={searchInputRef}
          placeholder={isInstantSearch ? "Pesquisar condições de pagamento (busca automática)" : "Pesquisar condições de pagamento"}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: 8, width: 300 }}
          onPressEnter={handleSearch}
          autoFocus
          allowClear={true}
        />
        {!isInstantSearch && (
          <Button 
            type="primary" 
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Pesquisar
          </Button>
        )}
        <Button type="primary" style={{ marginLeft: 'auto' }} onClick={handleCreatePaymentCondition}>
          Criar Condição de Pagamento
        </Button>
      </div>
      <Spin spinning={loading}>
        <Table
          dataSource={paymentConditions}
          columns={columns}
          rowKey="id"
          pagination={false}
        />
      </Spin>
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={totalCount}
        locale={{ items_per_page: '/ página' }}
        onChange={(page, pageSize) => {
          setCurrentPage(page);
          setPageSize(pageSize);
        }}
      />
      <Modal
        title={isEditMode ? "Editar Condição de Pagamento" : "Criar Condição de Pagamento"}
        open={isFormModalVisible}
        onCancel={handleFormCancel}
        footer={null}
        maskClosable={false}
        getContainer={false}
      >
        <PaymentConditionForm
          initialValues={selectedPaymentCondition}
          isEditMode={isEditMode}
          onFinish={handleSave}
          onCancel={handleFormCancel}
          errorMessage={errorMessage}
        />
      </Modal>
      <Modal
        title="Confirmação"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        maskClosable={false}
        getContainer={false}
      >
        <p>Tem certeza que deseja continuar?</p>
      </Modal>
    </>
  );
};

export default PaymentConditions;
